/**
 * Cliente de Meus Pontos — backend no geocoder (FastAPI), exposto em /geo/v1.
 * Auth via cookie de sessão do Traccar (mesma origem → enviado automaticamente).
 */

export interface NautPoint {
  id: number;
  user_id: number;
  company_id: number | null;
  name: string;
  type: string | null;
  description: string | null;
  latitude: number;
  longitude: number;
  visibility: string;
  icon: string | null;
  color: string | null;
  is_favorite: boolean;
  source: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  distance_m?: number | null;
  distance_nm?: number | null;
  bearing?: string | null;
  referencia_nautica?: string | null;
}

export interface PointInput {
  name: string;
  type?: string | null;
  description?: string | null;
  latitude: number;
  longitude: number;
  visibility?: string;
  icon?: string | null;
  color?: string | null;
  is_favorite?: boolean;
  source?: string | null;
}

const BASE = '/geo/v1/points';

async function req<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, { credentials: 'include', ...init });
  if (!r.ok) throw new Error(`points ${r.status}`);
  if (r.status === 204) return undefined as T;
  return r.json() as Promise<T>;
}

export const pointsApi = {
  list: (params?: { type?: string; q?: string }) => {
    const p = new URLSearchParams();
    if (params?.type) p.set('type', params.type);
    if (params?.q) p.set('q', params.q);
    const qs = p.toString();
    return req<NautPoint[]>(`${BASE}${qs ? `?${qs}` : ''}`);
  },
  nearby: (lat: number, lon: number, raio = 500_000, limit = 300) =>
    req<NautPoint[]>(`${BASE}/nearby?lat=${lat}&lon=${lon}&raio=${raio}&limit=${limit}`),
  distance: (id: number, lat: number, lon: number) =>
    req<{ id: number; distance_m: number; distance_km: number; distance_nm: number; bearing_deg: number | null; bearing: string | null }>(
      `${BASE}/${id}/distance?lat=${lat}&lon=${lon}`,
    ),
  get: (id: number) => req<NautPoint>(`${BASE}/${id}`),
  create: (body: PointInput) =>
    req<NautPoint>(BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  update: (id: number, body: PointInput) =>
    req<NautPoint>(`${BASE}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  remove: (id: number) => req<void>(`${BASE}/${id}`, { method: 'DELETE' }),
};

export interface PointType {
  key: string;
  label: string;
  color: string;
  emoji: string;
}

export const POINT_TYPES: PointType[] = [
  { key: 'pesca', label: 'Ponto de pesca', color: '#1f9d55', emoji: '🎣' },
  { key: 'mergulho', label: 'Mergulho', color: '#0ea5e9', emoji: '🤿' },
  { key: 'marina', label: 'Marina', color: '#007AFF', emoji: '⚓' },
  { key: 'ilha', label: 'Ilha', color: '#16a34a', emoji: '🏝️' },
  { key: 'praia', label: 'Praia', color: '#f59e0b', emoji: '🏖️' },
  { key: 'baia', label: 'Baía', color: '#0891b2', emoji: '🌊' },
  { key: 'enseada', label: 'Enseada', color: '#0891b2', emoji: '🌊' },
  { key: 'canal', label: 'Canal', color: '#6366f1', emoji: '🚤' },
  { key: 'rampa', label: 'Rampa de acesso', color: '#8b5cf6', emoji: '🛥️' },
  { key: 'fundeio', label: 'Fundeio', color: '#f97316', emoji: '⚓' },
  { key: 'risco', label: 'Área de risco', color: '#ef4444', emoji: '⚠️' },
  { key: 'proibida', label: 'Área proibida', color: '#dc2626', emoji: '⛔' },
  { key: 'turistico', label: 'Ponto turístico', color: '#ec4899', emoji: '📸' },
  { key: 'personalizado', label: 'Personalizado', color: '#64748b', emoji: '📍' },
];

export function pointType(key: string | null | undefined): PointType {
  return POINT_TYPES.find((t) => t.key === key) ?? POINT_TYPES[POINT_TYPES.length - 1];
}
